import paper from 'paper';
import { Point, Path } from 'paper';
import store from '../store';
import Log from 'loglevel';
import PainterService from '@/services/PainterService';
import PathStyle from '@/services/PathStyle';
import { EventBus } from '@/services/EventBus';
var util = require('util');
var parseString = util.promisify(require('xml2js').parseString);

const MAX_INTERSECTIONS = 20;
const DEFAULT_WIDTH = 500;
const DEFAULT_HEIGHT = 300;
const QUICK_ANALYSIS_COMPLETE = 100;
const MAX_ITEMS_TO_COMPARE = 600;
const MIN_CORNER_CROSSING_LENGTH = 0.1;

const delay = ms => new Promise(res => setTimeout(res, ms));

export default {
    // ui only
    async clearProject() {
        let allShapesIds = store.getters.shapes.map(s => s.id);
        await store.dispatch('removeShapeIds', allShapesIds);
        paper.project.layers.selectable.removeChildren();
        paper.project.layers.view.removeChildren();
        paper.project.layers.blueprint.removeChildren();
        paper.project.layers.import.removeChildren();
    },

    async importSVG (svgData, layer) {
        if (typeof svgData == 'undefined' || svgData.length == 0) {
            Log.warn('No SVG data to import');
            return;
        }

        this.autoZoomOn([]);

        let canvas = document.getElementById('js-editor');
        let canvasSize = {
            width: parseInt(canvas.width),
            height: parseInt(canvas.height)
        }
        let svgSize = await this.getGeometry(svgData);

        layer.importSVG(svgData, function(item) {
            item.scale(svgSize.width / canvasSize.width, svgSize.height / canvasSize.height);
        });
        await this.removeEmptyPaths(layer);
        await this.removeGroups(layer);
        await this.ignoreAll(layer);

        layer.strokeScaling = false;

        this.autoZoomOn(layer.getItems());
    },

    async loadDxfImport(dxfImport, layer) {
        this.autoZoomOn([]);

        let canvas = document.getElementById('js-editor');
        let canvasSize = {
            width: parseInt(canvas.width),
            height: parseInt(canvas.height)
        }
        let svgSize = dxfImport.report.geometry;

        await layer.importJSON(dxfImport.report.importLayer, function (item) {
            item.scale(svgSize.width / canvasSize.width, svgSize.height / canvasSize.height);
        });
        await this.removeEmptyPaths(layer);
        await this.removeGroups(layer);
        await this.ignoreAll(layer);

        layer.strokeScaling = false;

        this.autoZoomOn(layer.getItems());
    },

    async updateBlueprint(layer, blueprintLayer, callback) {
        store.dispatch('setLoadingStep', 'Analyse rapide');
        blueprintLayer.removeChildren();

        let selectedItems = layer.getItems({ name: 'selected' });

        selectedItems.forEach(item => {
            let copy = item.copyTo(blueprintLayer);
            copy.name = '';
        });

        let report = {};
        report.nbItems = selectedItems.length;

        if (selectedItems.length == 0) {
            store.dispatch('setLoadingStep', '');
            store.dispatch('setCleaningReport', report);
            if (callback) callback(report);
            return;
        }

        await delay(1);
        await this.quickAnalysis(blueprintLayer, report);

        if (report.quickAnalysis.pass) {
            store.dispatch('setLoadingStep', 'Jointure des traits');
            await delay(1);
            await this.joinPaths(blueprintLayer, report);

            store.dispatch('setLoadingStep', 'Simplification des formes');
            await delay(1);
            await this.simplify(blueprintLayer, report);

            store.dispatch('setLoadingStep', 'Analyse');
            await delay(1);
            await this.orientClockwise(blueprintLayer, report);
            await this.regroupPaths(blueprintLayer, report);
            await this.removeEmptyGroups(blueprintLayer);


            await this.detectCrossings(blueprintLayer, report);
            await this.detectOpenPaths(blueprintLayer, report);

            store.dispatch('setCleaningReport', report);
            PainterService.showBlueprint();
            PainterService.afterZoom();
            store.dispatch('setLoadingStep', '');
            if (callback) callback(report);
            EventBus.$emit('blueprintUpdated');
        } else {
            store.dispatch('setLoadingStep', '');
            store.dispatch('setCleaningReport', report);
            if (callback) callback(report);
        }
    },

    async quickAnalysis(layer, report) {
        report.quickAnalysis = {};
        let start = Date.now();

        let allPaths = layer
            .getItems({ class: 'Path' });

        let nbCompare = 0;
        let intersections = 0;

        if (allPaths.length < QUICK_ANALYSIS_COMPLETE) {
            report.quickAnalysis.mode = 'complete';
            for (let i = 0; i < allPaths.length - 1; i++) {
                for (let j = i; j < allPaths.length; j++) {
                    intersections += allPaths[i].getCrossings(allPaths[j]).length;
                    nbCompare++;
                    if (intersections > MAX_INTERSECTIONS) break;
                }
                if (intersections > MAX_INTERSECTIONS) break;
            }
        }
        else {
            report.quickAnalysis.mode = 'sorted';
            allPaths.sort((a, b) => b.length - a.length);

            let nbItems = allPaths.length;

            if (allPaths.length > MAX_ITEMS_TO_COMPARE) {
                nbItems = MAX_ITEMS_TO_COMPARE;
                report.quickAnalysis.mode = 'truncated';
            }

            for (let i = 0; i < nbItems - 1; i++) {
                for (let j = i; j < nbItems; j++) {
                    intersections += allPaths[i].getCrossings(allPaths[j]).length;
                    nbCompare++;
                    if (intersections > MAX_INTERSECTIONS) break;
                }
                if (intersections > MAX_INTERSECTIONS) break;
            }
        }

        report.quickAnalysis.nbCompare = nbCompare;
        report.quickAnalysis.nbIntersections = intersections;
        report.quickAnalysis.pass = intersections <= MAX_INTERSECTIONS;
        report.quickAnalysis.duration = Date.now() - start;
    },

    async expandJoinedItems(item, allItems, joinTolerance) {
        if (item.className != 'Path') return [item];
        let path = item;
        if (path.length > 3 && path.firstSegment.point.isClose(path.lastSegment.point, joinTolerance)) {
            path.closePath();
            return [path];
        }

        let localTolerance = joinTolerance;
        let possibleLoops = [];
        let seenPathIds;

        while (possibleLoops.length < 1 && localTolerance <= 0.1) {
            [possibleLoops, seenPathIds] = this.findJoinablePaths(path.lastSegment.point, allItems, [path.id], [path.id], [path.firstSegment.point], localTolerance);
            localTolerance *= 10;
        }

        seenPathIds.length;

        if (possibleLoops.length == 0) return [path];

        let commonItems = [...possibleLoops[0]];

        for (let i = 1; i < possibleLoops.length; i++) {
            commonItems = commonItems.filter(id => possibleLoops[i].indexOf(id) > -1);
        }

        return paper.project.layers.import.getItems({
            class: 'Path',
            id: id => commonItems.indexOf(id) > -1
        })
    },

    findJoinablePaths(point, paths, seenPathIds, pathChain, points, joinTolerance) {
        if (pathChain.length > 10000) {
            console.error('Chain too long')
            return [];
        }

        for (let i = 0; i < points.length; i++) {
            if (point.isClose(points[i], joinTolerance)) {
                if (i == 0) {
                    //console.debug("Complete loop found", pathChain);
                    return [[pathChain], seenPathIds];
                } else {
                    //console.debug("Inner loop found", pathChain);
                    return [[], seenPathIds];
                }
            }
        }


        let nextSteps = [];
        paths.filter(i => seenPathIds.indexOf(i.id) == -1 && pathChain.indexOf(i.id) == -1 && i.firstSegment.point.isClose(point, joinTolerance))
            .forEach(p => nextSteps.push({ id: p.id, nextPoint: p.lastSegment.point }));

        paths.filter(i => seenPathIds.indexOf(i.id) == -1 && pathChain.indexOf(i.id) == -1 && i.lastSegment.point.isClose(point, joinTolerance))
            .forEach(p => nextSteps.push({ id: p.id, nextPoint: p.firstSegment.point }));

        if (nextSteps.length == 0) {
            //console.debug("dead end", pathChain, joinTolerance);
            return [[], seenPathIds];
        }

        let possibleLoops = [];
        for (let i = 0; i < nextSteps.length; i++) {
            let step = nextSteps[i];

            let loops;
            [loops, seenPathIds] = this.findJoinablePaths(step.nextPoint, paths, [...seenPathIds, step.id], [...pathChain, step.id], [...points, point], joinTolerance);

            if (loops.length > 0) {
                possibleLoops = [...possibleLoops, ...loops];
            }
        }

        return [possibleLoops, seenPathIds];
    },

    async detectClosedShapes(items, joinTolerance) {
        let closedShapes = items.filter(i => i.className != 'Path');
        let allPaths = items.filter(i => i.className == 'Path');

        let seenPathIds = [];
        allPaths.forEach(p => {
            if (seenPathIds.indexOf(p.id) > -1) {
                return;
            }
            let itemsLoop = [];
            let localTolerance = joinTolerance;
            while (itemsLoop.length < 1 && localTolerance <= 1) {
                [itemsLoop, seenPathIds] = this.expandJoinedItemsIn(p, allPaths, seenPathIds, localTolerance);
                localTolerance *= 10;
            }
            if (itemsLoop.length > 0) {
                closedShapes = closedShapes.concat(itemsLoop);
            }
        });

        return closedShapes;
    },

    expandJoinedItemsIn(path, paths, seenPathIds, joinTolerance) {
        if (path.length > 3 && path.firstSegment.point.isClose(path.lastSegment.point, joinTolerance)) {
            path.closePath();
            return [[path], []];
        }
        let possibleLoops;

        [possibleLoops, seenPathIds] = this.findJoinablePaths(path.lastSegment.point, paths, seenPathIds, [path.id], [path.firstSegment.point], joinTolerance);

        if (possibleLoops.length == 1) {
            let loop = possibleLoops[0];

            return [paths.filter(p => loop.indexOf(p.id) > -1), seenPathIds];
        }
        return [[], seenPathIds];
    },

    async joinPaths(layer, report) {
        report.join = {};
        let start = Date.now();

        report.join.count = 0;
        let pathsToJoin = layer.getItems({ class: 'Path' });

        report.join.countBefore = pathsToJoin.length;

        await this.joinOpenPaths(pathsToJoin, report);
        report.join.duration = Date.now() - start;
    },

    joinOpenPaths(pathsToJoin, report) {
        let tolerance = 0.000000001;

        while (tolerance <= 0.1 && pathsToJoin.length > 0) {
            let openPaths = [];
            for (let pi = 0; pi < pathsToJoin.length; pi++) {
                let path = pathsToJoin[pi];

                if (path.firstSegment.point.isClose(path.lastSegment.point, tolerance)) {
                    path.closePath();
                    continue;
                }

                let [matchFirstIndex, matchLastIndex] = this.matchingPathsIndexes(path, openPaths, tolerance);

                // No match
                if (matchFirstIndex.length != 1 && matchLastIndex.length != 1) {
                    openPaths.push(path);
                    continue;
                }

                // Match only last
                if (matchFirstIndex.length != 1) {
                    openPaths[matchLastIndex[0]].join(path, tolerance);
                    if (report) report.join.count++;
                    continue;
                }

                // Match only first
                if (matchLastIndex.length != 1) {
                    openPaths[matchFirstIndex[0]].join(path, tolerance);
                    if (report) report.join.count++;
                    continue;
                }

                // Link between two paths
                if (matchFirstIndex[0] !== matchLastIndex[0]) {
                    openPaths[matchFirstIndex[0]].join(path, tolerance);
                    openPaths[matchFirstIndex[0]].join(openPaths[matchLastIndex[0]], tolerance);
                    if (report) report.join.count++;
                    openPaths.splice(matchLastIndex[0], 1);
                    continue;
                }

                // Closing one path
                openPaths[matchFirstIndex[0]].join(path, tolerance);
                openPaths.splice(matchFirstIndex[0], 1);
            }
            tolerance *= 10;
            pathsToJoin = openPaths;
        }

        if (pathsToJoin.length > 0) {
            pathsToJoin.forEach(path => {
                if (path.firstSegment.point.isClose(path.lastSegment.point, tolerance)) {
                    path.closePath();
                }
            })
        }
    },

    matchingPathsIndexes(path, openPaths, epsilon) {
        let matchFirstIndex = [], matchLastIndex = [];
        for (let i = 0; i < openPaths.length; i++) {
            if (
                openPaths[i].firstSegment.point.isClose(path.firstSegment.point, epsilon)
                || openPaths[i].lastSegment.point.isClose(path.firstSegment.point, epsilon)
            ) {
                matchFirstIndex.push(i);
            }

            if (
                openPaths[i].firstSegment.point.isClose(path.lastSegment.point, epsilon)
                || openPaths[i].lastSegment.point.isClose(path.lastSegment.point, epsilon)
            ) {
                matchLastIndex.push(i);
            }
        }
        return [matchFirstIndex, matchLastIndex];
    },

    async simplify(layer, report) {
        report.removeFine = { count: 0 };
        let epsilon = 0.001;
        let minLength = 0.5;
        let start = Date.now();
        let varLength, simplifyLength;

        layer.getItems({ class: 'Path' }).forEach(p => {
            p.reduce();
            if (this.isCircle(p, epsilon * 10)) {
                let center = p.bounds.center;
                let radius = p.bounds.width / 2;
                let circle = new Path.Circle(center, radius);
                PathStyle.copyStyle(circle, p);
                layer.addChild(circle);
                p.remove();
                return;
            }
            let curve = p.firstCurve;
            let hasLooped = false;

            while (curve != null && curve.next != null && (curve.next.index > curve.index || !hasLooped)) {

                let curvesToSimplify = [];
                varLength = minLength;
                simplifyLength = 0;
                if (curve.next.index < curve.index ) hasLooped = true;

                // Extraire une séquence de petits traits
                while (curve.next != null && curve.next.length < varLength && curve.next.index > curve.index && this.noSharpAngle(curve)) {
                    curvesToSimplify.push(curve.next);
                    simplifyLength += curve.next.length;
                    varLength = simplifyLength * 1.2 / (curvesToSimplify.length);
                    curve = curve.next;
                }

                curvesToSimplify = curvesToSimplify.filter(c => c.length < varLength);

                if (curvesToSimplify.length > 2) {
                    let segments = [curvesToSimplify[0].segment1];
                    curvesToSimplify.forEach(c => segments.push(c.segment2));

                    let path = curve.path;
                    let simple = new Path(segments);

                    let arc = this.getArc(simple, epsilon);

                    if (arc) {
                        if (arc.segments.length < segments.length - 1) {
                            curve = this.replaceSimplified(path, arc);
                        }
                    }
                    else {
                        simple.flatten(0.001);
                        simple.simplify(0.001);
                        if (simple.segments.length < segments.length - 1) {
                            curve = this.replaceSimplified(path, simple);
                        }
                    }
                }
                if (curve.next && curve.next.index > curve.index) curve = curve.next;
            }

            curve.path.curves.forEach(c => {
                if (c.length < epsilon) c.remove();
            })
        });
        report.removeFine.duration = Date.now() - start;
    },

    noSharpAngle(curve) {
        let before = curve.getTangentAtTime(0.99).angle,
            after = curve.next.getTangentAtTime(0.01).angle,
            positiveBefore = before < 0 ? before + 180 : before,
            positiveAfter = after < 0 ? after + 180 : after;

        return Math.abs(positiveAfter - positiveBefore) < 60;
    },

    replaceSimplified(path, simple) {
        PathStyle.copyStyle(simple, path);

        let startPoint = simple.firstSegment.point,
            endPoint = simple.lastSegment.point;

        let start = path.getNearestLocation(startPoint);
        path.splitAt(start);
        let end = path.getNearestLocation(endPoint);
        let rest = path.splitAt(end);

        if (rest) {
            let deltaRest = Math.abs(rest.length - simple.length),
                deltaP = Math.abs(path.length - simple.length);

            if (deltaRest < deltaP / 2) {
                rest.remove();
                path.join(simple);
                path.closePath();
                return path.getNearestLocation(endPoint).curve.next;
            } else {
                path.remove();
                rest.join(simple);
                rest.closePath();
                return rest.getNearestLocation(endPoint).curve.next;
            }
        } else {
            path.join(simple);
            path.closePath();
            return path.getNearestLocation(endPoint).curve.next;
        }
    },

    isCircle(path, epsilon) {

        let bounds = path.bounds;
        if (Math.abs(bounds.width - bounds.height) > epsilon * 10) return false;
        let radius = bounds.width / 2;

        let center = bounds.center;
        let minRadius = radius - (epsilon * 10),
            maxRadius = radius + (epsilon * 10);

        if (path.segments.some(s => {
            let l = s.point.subtract(center).length;
            return l < minRadius || l > maxRadius;
        })
        ) {
            return false;
        }
        return true;
    },

    getArc(path, epsilon) {
        let center, radius, p1, p2, p3;

        p1 = path.firstSegment.point;
        p3 = path.lastSegment.point;
        p2 = path.getPointAt(path.length / 2);

        let circle = this.getCircleBase(p1, p2, p3);
        center = circle.center;
        radius = circle.radius;

        let minRadius = radius - epsilon - 0.01,
            maxRadius = radius + epsilon + 0.01;

        if (path.segments.some(p => {
            let l = p.point.subtract(center).length;
            return l < minRadius || l > maxRadius;
        })) return null;

        let arc;
        try {
            arc = new Path.Arc(p1, p2, p3);
        }
        catch (e) {
            console.log('Oh, ohhhh', p1, p2, p3);
        }

        return arc;
    },


    getCircleBase(p1, p2, p3) {
        // http://www.ambrsoft.com/trigocalc/circle3d.htm
        let x1 = p1.x,
            y1 = p1.y,
            x2 = p2.x,
            y2 = p2.y,
            x3 = p3.x,
            y3 = p3.y,
            x1s = Math.pow(x1, 2),
            y1s = Math.pow(y1, 2),
            x2s = Math.pow(x2, 2),
            y2s = Math.pow(y2, 2),
            x3s = Math.pow(x3, 2),
            y3s = Math.pow(y3, 2),
            A = x1 * (y2 - y3) - y1 * (x2 - x3) + x2 * y3 - x3 * y2,
            B = (x1s + y1s) * (y3 - y2) + (x2s + y2s) * (y1 - y3) + (x3s + y3s) * (y2 - y1),
            C = (x1s + y1s) * (x2 - x3) + (x2s + y2s) * (x3 - x1) + (x3s + y3s) * (x1 - x2),
            x = -B / (2 * A),
            y = -C / (2 * A),
            radius = Math.sqrt(Math.pow(x - x1, 2) + Math.pow(y - y1, 2)),
            center = new paper.Point(x, y);

        return {
            center,
            radius,
            error: A == 0
        };
    },

    async orientClockwise(layer, report) {
        report.reorient = {};
        let start = Date.now();
        let toReorient = layer
            .getItems({ class: 'Path' })
            .filter(path => !path.clockwise);

        toReorient.forEach(path => path.reorient(true, true));
        report.reorient.count = toReorient.length;
        report.reorient.duration = Date.now() - start;
    },

    async removeGroups(layer) {
        layer.addChildren(layer.getItems({ class: 'Path' }));
        layer.addChildren(layer.getItems({ class: 'Shape' }));
        layer.getItems({ class: 'Group' }).forEach(g => g.remove());
    },

    async removeEmptyPaths(layer) {
        layer.getItems({ length: 0 }).forEach(p => p.remove());
    },

    async selectAll(layer) {
        layer.getItems({ class: 'Path' }).forEach(i => i.name = 'selected');
        layer.getItems({ class: 'Shape' }).forEach(i => i.name = 'selected');
    },

    async ignoreAll(layer) {
        layer.getItems({ class: 'Path' }).forEach(i => i.name = 'ignored');
        layer.getItems({ class: 'Shape' }).forEach(i => i.name = 'ignored');
    },

    async regroupPaths(layer, report) {
        report.regroup = {};
        let start = Date.now();

        let allPaths = layer.getItems({ class: 'Path' });

        let count = 0;

        await allPaths.forEach(p => {
            let parentGroup = p.parent;
            let change = false;
            while (parentGroup.children.length == 1 && parentGroup.className == 'Group') {
                parentGroup = parentGroup.parent;
                change = true;
            }
            if (change) {
                p.addTo(parentGroup);
                count++;
            }
        });

        report.regroup.count = count;
        report.regroup.duration = Date.now() - start;
    },

    async removeEmptyGroups(layer) {

        let emptyGroups = layer.getItems({ class: 'Group', children: [] });
        await emptyGroups.forEach(g => {
            while (g.parent.children.length == 1) {
                g = g.parent;
            }
            g.remove();
        });
    },

    async detectCrossings(layer, report) {
        report.crossings = {};
        let start = Date.now();

        let allPaths = layer
            .getItems({ class: 'Path' });

        let intersections = [];
        for (let i = 0; i < allPaths.length; i++) {
            for (let j = i; j < allPaths.length; j++) {
                if(i == j){
                    intersections = intersections.concat(allPaths[i].getIntersections(allPaths[j], c => c.intersection.offset - c.offset > MIN_CORNER_CROSSING_LENGTH));

                }else{
                    intersections = intersections.concat(allPaths[i].getIntersections(allPaths[j]));

                }
            }
        }

        report.crossings.intersections = intersections.map(i => i.point);
        report.crossings.count = intersections.length;
        report.crossings.duration = Date.now() - start;
    },

    async detectOpenPaths(layer, report) {
        let start = Date.now();

        let openPaths = layer
            .getItems({ class: 'Path', closed: false });

        report.openPaths = {
            items: openPaths.map(i => i.id),
            count: openPaths.length,
            duration: Date.now() - start
        }
    },

    // ui only
    autoZoom() {
        this.autoZoomOn(paper.project.layers.view.getItems());
    },

    autoZoomOn(items) {
        let minTop = 99999999, maxBottom = -99999999, minLeft = 9999999, maxRight = -99999999;
        let center;

        let canvas = document.getElementById('js-editor');
        let workspaceWidth = parseInt(canvas.width);
        let workspaceHeight = parseInt(canvas.height);


        if (items.length == 0 || (items.length == 1 && items[0].name == 'origin')) {
            minTop = -DEFAULT_HEIGHT;
            maxBottom = 0;
            minLeft = 0;
            maxRight = DEFAULT_WIDTH;

            let centerRatio = store.getters.mobile ? 0.2 : 0.6;

            let defaultRatio = DEFAULT_WIDTH / DEFAULT_HEIGHT;
            let viewRatio = workspaceWidth / workspaceHeight;
            if (defaultRatio > viewRatio) {
                center = new Point((DEFAULT_WIDTH) * centerRatio, -(DEFAULT_HEIGHT) * centerRatio * defaultRatio / viewRatio);
            }
            else {
                center = new Point((DEFAULT_WIDTH) * centerRatio * viewRatio / defaultRatio, -(DEFAULT_HEIGHT) * centerRatio);
            }

        } else {
            items.forEach(item => {
                let bounds = item.bounds;
                if (bounds.area == 0) return;
                if (bounds.top < minTop) minTop = bounds.top;
                if (bounds.bottom > maxBottom) maxBottom = bounds.bottom;
                if (bounds.left < minLeft) minLeft = bounds.left;
                if (bounds.right > maxRight) maxRight = bounds.right;
            });
            center = new Point((maxRight + minLeft) / 2, (maxBottom + minTop) / 2);
        }


        let factorX = workspaceWidth * 0.8 / (maxRight - minLeft);
        let factorY = workspaceHeight * 0.8 / (maxBottom - minTop);

        let zoom = (factorX < factorY ? factorX : factorY) / 2;
        store.dispatch('setZoomCenter', {zoom, center});
        PainterService.displayPosition();
    },

    trianglePath(rectangle, heightPos) {
        let point = rectangle.topLeft;
        let size = rectangle.size;
        let aVector = new Point({ x: 0, y: size.height }),
            bVector = new Point({ x: heightPos * size.width, y: 0 }),
            cVector = new Point({ x: size.width, y: size.height });

        let ret = new Path();
        ret.add(point.add(aVector));
        ret.add(point.add(bVector));
        ret.add(point.add(cVector));
        ret.closePath();
        return ret;
    },

    async getGeometry(svgData) {
        let result = await parseString(svgData);

        let width = result.svg['$'].width;
        let height = result.svg['$'].height;

        let onlyNumbers = /^[\d.]{0,15}$/;

        if (typeof width === 'undefined' || !onlyNumbers.test(width)
            || typeof height === 'undefined' || !onlyNumbers.test(height)
        ) {
            let originalViewBox = result.svg['$'].viewBox;
            [, , width, height] = originalViewBox.split(' ');
        }

        return { width, height };
    }
}
